<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import SearchSelect from "@/Components/SearchSelect.vue";
import SelectInput from "@/Components/SelectInput.vue";
import { computed } from "vue";

const props = defineProps({
  filterValues: Object,
  sorting: Object,
  searching: Boolean,

  projectManagers: Array,
});

const emit = defineEmits(["search", "reset"]);

// ── Date helpers (linux ts seconds) ─────────────────────────────
const pad2 = (n) => String(n).padStart(2, "0");

const tsToInputDate = (ts) => {
  if (!ts && ts !== 0) return "";
  const d = new Date(Number(ts) * 1000);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const inputDateToTs = (yyyyMMdd) => {
  const s = (yyyyMMdd || "").toString().trim();
  if (!s) return null;

  const [y, m, d] = s.split("-").map((x) => Number(x));
  if (!y || !m || !d) return null;

  const date = new Date(y, m - 1, d, 12, 0, 0);
  const ts = Math.floor(date.getTime() / 1000);
  return Number.isFinite(ts) ? ts : null;
};

// Start date range bindings
const startDateFromModel = computed({
  get: () => tsToInputDate(props.filterValues?.start_date_from),
  set: (v) => {
    props.filterValues.start_date_from = inputDateToTs(v);
  },
});

const startDateToModel = computed({
  get: () => tsToInputDate(props.filterValues?.start_date_to),
  set: (v) => {
    props.filterValues.start_date_to = inputDateToTs(v);
  },
});

// Due date range bindings
const dueDateFromModel = computed({
  get: () => tsToInputDate(props.filterValues?.date_due_from),
  set: (v) => {
    props.filterValues.date_due_from = inputDateToTs(v);
  },
});

const dueDateToModel = computed({
  get: () => tsToInputDate(props.filterValues?.date_due_to),
  set: (v) => {
    props.filterValues.date_due_to = inputDateToTs(v);
  },
});

// ── Project Manager SearchSelect ───────────────────────────────
const pmOptions = computed(() => {
  const out = [];
  const seen = new Set();

  for (const pm of props.projectManagers || []) {
    const rawId = pm?.employeeID ?? null;
    const value = rawId == null ? null : Number(rawId);
    if (value == null || Number.isNaN(value) || seen.has(value)) continue;

    const fn = (pm?.firstname ?? "").toString().trim();
    const ln = (pm?.lastname ?? "").toString().trim();
    const label = `${fn} ${ln}`.trim() || `#${value}`;

    seen.add(value);
    out.push({ label, value });
  }

  return out;
});

const pmModel = computed({
  get: () =>
    props.filterValues?.employeeID !== null &&
    props.filterValues?.employeeID !== undefined &&
    String(props.filterValues.employeeID).trim() !== ""
      ? Number(props.filterValues.employeeID)
      : null,
  set: (v) => {
    props.filterValues.employeeID = v != null ? Number(v) : null;
  },
});

// ── Successful Lead select (tri-state) ─────────────────────────
const successfulOptions = [
  { label: "All", value: null },
  { label: "Yes", value: 1 },
  { label: "No", value: 0 },
];

const successfulModel = computed({
  get: () => {
    const v = props.filterValues?.successful_lead;
    if (v === null || v === undefined || String(v).trim() === "") return null;
    if (v === true || v === 1 || v === "1") return 1;
    if (v === false || v === 0 || v === "0") return 0;
    return null;
  },
  set: (v) => {
    props.filterValues.successful_lead = v === null ? null : Number(v);
  },
});
</script>

<template>
  <!-- Client -->
  <div>
    <InputLabel value="Client" />
    <TextInput
      id="client"
      type="text"
      class="mt-1 w-full"
      placeholder="Client"
      v-model="filterValues.client"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Contact name -->
  <div>
    <InputLabel value="Contact name" />
    <TextInput
      id="contact_name"
      type="text"
      class="mt-1 w-full"
      placeholder="Contact name"
      v-model="filterValues.contact_name"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Contact surname -->
  <div>
    <InputLabel value="Contact surname" />
    <TextInput
      id="contact_surname"
      type="text"
      class="mt-1 w-full"
      placeholder="Contact surname"
      v-model="filterValues.contact_surname"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Contact email -->
  <div>
    <InputLabel value="Contact email" />
    <TextInput
      id="contact_email"
      type="text"
      class="mt-1 w-full"
      placeholder="Contact email"
      v-model="filterValues.contact_email"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Contact phone -->
  <div>
    <InputLabel value="Contact phone" />
    <TextInput
      id="contact_number"
      type="text"
      class="mt-1 w-full"
      placeholder="Contact phone"
      v-model="filterValues.contact_number"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Start date (From / To) -->
  <div>
    <InputLabel value="Start date (from)" />
    <TextInput
      id="start_date_from"
      type="date"
      class="mt-1 w-full"
      v-model="startDateFromModel"
      @change="emit('search')"
      :disabled="searching"
    />
  </div>

  <div>
    <InputLabel value="Start date (to)" />
    <TextInput
      id="start_date_to"
      type="date"
      class="mt-1 w-full"
      v-model="startDateToModel"
      @change="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Project Manager -->
  <div>
    <InputLabel value="Project manager" />
    <SearchSelect
      v-model="pmModel"
      :options="pmOptions"
      placeholder="Please select"
      :disabled="searching"
      @change="emit('search')"
    />
  </div>

  <!-- Due date (From / To) -->
  <div>
    <InputLabel value="Due date (from)" />
    <TextInput
      id="date_due_from"
      type="date"
      class="mt-1 w-full"
      v-model="dueDateFromModel"
      @change="emit('search')"
      :disabled="searching"
    />
  </div>

  <div>
    <InputLabel value="Due date (to)" />
    <TextInput
      id="date_due_to"
      type="date"
      class="mt-1 w-full"
      v-model="dueDateToModel"
      @change="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Successful lead -->
  <div>
    <InputLabel value="Successful lead" />
    <SelectInput
      id="successful_lead"
      class="mt-1"
      v-model="successfulModel"
      @change="emit('search')"
      :disabled="searching"
    >
      <option v-for="o in successfulOptions" :key="String(o.value)" :value="o.value">
        {{ o.label }}
      </option>
    </SelectInput>
  </div>

  <!-- Notes -->
  <div>
    <InputLabel value="Notes" />
    <TextInput
      id="notes"
      type="text"
      class="mt-1 w-full"
      placeholder="Notes"
      v-model="filterValues.notes"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Action plan -->
  <div>
    <InputLabel value="Action plan" />
    <TextInput
      id="action_plan"
      type="text"
      class="mt-1 w-full"
      placeholder="Action plan"
      v-model="filterValues.action_plan"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Reset -->
  <div>
    <InputLabel value="Reset Filter" class="text-gray-100" />
    <SecondaryButton class="mt-1 h-[42px]" @click="emit('reset')" :disabled="searching">
      Reset Filters
    </SecondaryButton>
  </div>
</template>
